import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const HERO_BG_STYLE = { backgroundImage: `url(${process.env.PUBLIC_URL}/assets/hero-railtracks.jpg)` };

const programs = [
  {
    id: 'poc-01',
    num: '01',
    cat: 'Overwatch.',
    tagline: 'Automated Security Sweeps',
    title: <>Pre-programmed patrols of <em>key corridors.</em></>,
    lede:
      'Drones fly predefined routes across rail corridors, conducting routine monitoring inside safe, designated flight zones — fully integrated with dispatch.',
    bullets: [
      <><strong>Pre-programmed Routes.</strong> Drones follow predefined flight paths covering key rail corridors.</>,
      <><strong>Routine Inspections.</strong> Conduct scheduled patrols for track monitoring and infrastructure checks.</>,
      <><strong>Geofencing for Safety.</strong> Ensures drones remain within designated flight zones and avoid no-fly areas.</>,
      <><strong>AI-Driven Navigation.</strong> Uses obstacle detection and avoidance to prevent collisions.</>,
      <><strong>Integration with Rail Networks.</strong> Can be linked to railroad dispatch systems for proactive monitoring.</>
    ],
    visual: {
      label: '[ POC · 01 ] Patrol Route',
      video: '/assets/overwatch.mp4'
    }
  },
  {
    id: 'poc-02',
    reverse: true,
    num: '02',
    cat: 'Response.',
    tagline: 'Drone as First Responder (DFR)',
    title: <>Eyes on scene <em>before</em> the crew arrives.</>,
    lede:
      'UAS deployed as the first asset on scene — assessing derailments, obstructions, and emergencies aerially while crews are still inbound.',
    bullets: [
      'Deploying drones as first responders to enhance safety, efficiency, and incident response in railroad operations.',
      <>Provides rapid aerial assessment of <strong>derailments, track obstructions, mechanical failures,</strong> and emergency situations.</>,
      <><strong>Live feed to dispatch &amp; field crews</strong> enables informed decision-making before personnel arrive.</>
    ],
    visual: {
      label: '[ POC · 02 ] Incident Scene',
      video: '/assets/response.mp4'
    }
  },
  {
    id: 'poc-03',
    num: '03',
    cat: 'Inventory.',
    tagline: 'Railyard Inventory Tracking',
    title: <>An <em>always-on</em> record of the yard.</>,
    lede:
      'A network of high-resolution fixed cameras paired with computer vision reads rail car numbers and reconciles movements in real time.',
    bullets: [
      <><strong>Camera Network Coverage.</strong> High-resolution cameras monitor storage tracks and movements.</>,
      <><strong>Image Capture &amp; Processing.</strong> AI extracts rail car numbers and verifies AEI reader data.</>,
      <><strong>Automated Data Logging.</strong> System updates rail yard inventory records in real-time.</>,
      <><strong>Movement Detection &amp; Reconciliation.</strong> Tracks rail car arrivals, departures, and reclassifications using AEI and image analytics.</>,
      <><strong>Instant Alerts &amp; Reports.</strong> Notifies operators of misplaced, delayed, or unauthorized rail car movements.</>
    ],
    visual: {
      label: '[ POC · 03 ] Yard Network',
      video: '/assets/inventory.mp4'
    }
  },
  {
    id: 'poc-04',
    reverse: true,
    num: '04',
    cat: 'Security.',
    tagline: 'Open Container Detections',
    title: <>Detect <em>tampering</em> on the move.</>,
    lede:
      'Vision systems identify open, partially open, or compromised containers as trains move through monitored zones — flagging risk in real time.',
    bullets: [
      <><strong>Fully Open Container Doors.</strong> High security risk and weather exposure.</>,
      <><strong>Partially Open Containers.</strong> Potential cargo shift or improper locking.</>,
      <><strong>Broken Seals or Missing Latches.</strong> Possible tampering or theft.</>,
      <><strong>Instant Notifications.</strong> Alerts sent to security teams, yard managers, or train crews.</>
    ],
    visual: {
      label: '[ POC · 04 ] Container Scan',
      video: '/assets/security.mp4'
    }
  },
  {
    id: 'poc-05',
    num: '05',
    cat: 'Mechanical.',
    tagline: 'Dragging Equipment Detection',
    title: <>Catch hazards <em>before</em> they damage track.</>,
    lede:
      'Wayside cameras and machine learning flag dragging hardware, loose brake rigging, and other ground-contact hazards before they cause cascading damage.',
    bullets: [
      <><strong>Early Hazard Detection.</strong> Prevents track damage, derailments, or service interruptions.</>,
      <><strong>Faster Inspection Process.</strong> Reduces manual walkarounds and increases efficiency.</>,
      <><strong>Targeted Maintenance.</strong> Crews arrive with the right tools and replacement parts.</>,
      <><strong>Improved Traffic Flow.</strong> Reduces unplanned stops and rail congestion.</>
    ],
    visual: {
      label: '[ POC · 05 ] Wayside Capture',
      video: '/assets/mechanical.mp4'
    }
  },
  {
    id: 'poc-06',
    reverse: true,
    num: '06',
    cat: 'Diagnostics.',
    tagline: 'Cause of Train Emergency Stops',
    title: <>From hours of walking to <em>minutes</em> of flying.</>,
    lede:
      'Drones plus image analytics rapidly diagnose the root cause of emergency stops — identifying broken draw bars, disconnected air hoses, couplers, or knuckles without sending crews trackside for hours.',
    bullets: [
      <>Leveraging <strong>drones, image analytics, and machine learning</strong> to quickly diagnose emergency stop causes.</>,
      <>Identifies critical failures such as <strong>broken draw bars, disconnected air hoses, broken couplers, or broken knuckles.</strong></>,
      <><strong>Faster Diagnosis.</strong> Reduces time spent manually inspecting long trains.</>,
      <><strong>Better Equipment Planning.</strong> Ensures repair teams arrive with the right parts and tools.</>,
      <><strong>Traffic Management.</strong> Allows dispatchers to optimize rail traffic flow during repairs.</>,
      <><strong>Safety Improvement.</strong> Minimizes risk for crew members performing on-site inspections.</>
    ],
    visual: {
      label: '[ POC · 06 ] Diagnostic Pass',
      video: '/assets/diagnostics.mp4'
    }
  }
];

function PocVisual({ visual }) {
  const videoRef = useRef(null);
  const [ended, setEnded] = useState(false);

  const handleReplay = () => {
    const v = videoRef.current;
    if (!v) return;
    setEnded(false);
    v.currentTime = 0;
    v.play();
  };

  if (visual.video) {
    return (
      <div className="poc-visual-wrap reveal d1">
        <div className="poc-visual-caption">{visual.label}</div>
        <div className="poc-visual poc-visual-video">
          <video
            ref={videoRef}
            className="poc-video"
            src={visual.video}
            autoPlay
            muted
            playsInline
            onEnded={() => setEnded(true)}
          />
          <div className="poc-video-frame"><i></i><b></b></div>
          {ended && (
            <button type="button" className="poc-replay" onClick={handleReplay} aria-label="Replay video">
              <span className="poc-replay-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="poc-visual reveal d1">
      <div className="vlabel">{visual.label}</div>
      {visual.crosshair && <div className="crosshair" />}
      {visual.target && <div className="target" />}
      <div className="corners"><i></i><b></b></div>
      {visual.comingSoon && (
        <div className="coming-soon">
          <div className="coming-soon-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 3" />
            </svg>
          </div>
          <div className="coming-soon-text">Visual Coming Soon</div>
        </div>
      )}
      <div className="vfooter">
        <span>{visual.footerLeft}</span>
        {visual.footerRight && <span className="placeholder">{visual.footerRight}</span>}
      </div>
    </div>
  );
}

export default function ProofOfConcept() {
  return (
    <>
      <section className="page-hero">
        <div className="page-hero-bg page-hero-bg-photo" style={HERO_BG_STYLE} aria-hidden="true" />
        <div className="wrap">
          <div className="crumbs">
            <Link to="/">Home</Link>
            <span className="sep">/</span>
            <span className="here">Proof of Concept</span>
          </div>

          <div className="page-hero-grid">
            <div>
              <div className="eyebrow reveal" style={{ marginBottom: 28 }}>
                RFS · Field Demonstrations
              </div>
              <h1 className="display h1 reveal d1" style={{ margin: '0 0 28px' }}>
                Proof of <em>concept.</em>
              </h1>
              <p className="lede reveal d2">
                A selection of field projects that demonstrate how our aerial systems, fixed cameras, and machine vision pipelines translate into measurable outcomes across rail operations.
              </p>
            </div>

            <aside className="page-meta reveal d2" aria-label="At a glance">
              <div className="page-meta-row"><span className="k">Programs</span><span className="v">06</span></div>
              <div className="page-meta-row"><span className="k">Discipline</span><span className="v">Inspection · Overwatch</span></div>
              <div className="page-meta-row"><span className="k">Status</span><span className="v">Active Demonstrations</span></div>
              <div className="page-meta-row"><span className="k">Theatre</span><span className="v">North American Rail</span></div>
            </aside>
          </div>
        </div>
      </section>

      <section className="poc-index">
        <div className="wrap">
          <div className="poc-index-inner">
            <div className="mono">[ Index · Six Programs ]</div>
            <nav className="poc-index-list">
              {programs.map((p, i) => (
                <a key={p.id} href={`#${p.id}`}>
                  <span className="n">{String(i + 1).padStart(2, '0')}</span>
                  <span className="t">{p.tagline}</span>
                </a>
              ))}
            </nav>
          </div>
        </div>
      </section>

      <section className="poc">
        {programs.map((p) => (
          <article
            key={p.id}
            id={p.id}
            className={`poc-item${p.reverse ? ' reverse' : ''}`}
          >
            <div className="wrap">
              <div className="poc-grid">
                <div className="poc-text reveal">
                  <div className="poc-cat-row">
                    <span className="poc-cat-num">{p.num} —</span>
                    <h2 className="poc-cat">{p.cat}</h2>
                  </div>
                  <div className="poc-tagline">{p.tagline}</div>
                  <h3 className="poc-title">{p.title}</h3>
                  <p className="poc-lede">{p.lede}</p>
                  <ul className="poc-list">
                    {p.bullets.map((b, idx) => (
                      <li key={idx}>
                        <span className="marker">{String(idx + 1).padStart(2, '0')}</span>
                        <span className="label">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <PocVisual visual={p.visual} />
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className="cta-band">
        <div className="cta-band-bg" aria-hidden="true" />
        <div className="wrap">
          <div className="cta-band-grid">
            <h2 className="cta-title reveal">
              Have a mission profile <br />in mind? Let's <em>talk.</em>
            </h2>
            <Link to="/#contact" className="cta-link reveal d1">
              <span>Contact Railway Flight Solutions</span>
              <span className="arrow">→</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
